<template>
  <q-dialog
    ref="dialog"
    :maximized="maximized"
    @hide="onDialogHide"
  >
    <div
      style="min-width:50vw;"
      class="overflow-hidden-x"
    >
      <q-card
        class="q-pa-sm column no-wrap justify-between"
        :class="{
          'dialog-height-mobile': $q.platform.is.mobile,
          'dialog-height-non-mobile': !$q.platform.is.mobile,
        }"
      >
        <q-card-section class="q-pb-none">
          <div class="row q-mb-md col-12 items-start">
            <div class="row col items-center">
              <div
                v-if="program.coverphoto"
                class="q-pr-md col-auto"
              >
                <q-img
                  :src="program.coverphoto"
                  :ratio="16/9"
                  style="max-height: 40px; width: 71px"
                  class="rounded-borders"
                />
              </div>
              <div class="col text-h6">
                <behavior-tracking-icon
                  v-if="isBehaviorTrackingProgram(program)"
                  size="md"
                />
                {{ program.programName }}
              </div>
            </div>
            <div
              v-if="!maximized"
              class="col-auto"
            >
              <q-btn
                flat
                round
                dense
                icon="open_in_full"
                color="dark"
                @click="onMaximize"
              >
                <q-tooltip>
                  {{ $t('common.buttonMaximize') }}
                </q-tooltip>
              </q-btn>
            </div>
            <div
              v-else-if="!$q.platform.is.mobile"
              class="col-auto"
            >
              <q-btn
                flat
                round
                dense
                icon="close_fullscreen"
                color="dark"
                @click="onCloseFullscreen"
              >
                <q-tooltip>
                  {{ $t('common.buttonCloseFullscreen') }}
                </q-tooltip>
              </q-btn>
            </div>
            <div
              class="col-auto"
            >
              <q-btn
                v-close-popup
                flat
                round
                dense
                color="dark"
                icon="close"
              />
            </div>
          </div>
          <q-tabs
            v-model="tab"
            active-color="primary"
            indicator-color="primary"
            align="justify"
            narrow-indicator
            dense
            class="text-dark col-12 q-mb-md"
            @update:model-value="onSwitchTab"
          >
            <q-tab
              :name="tabs.content"
              :label="$t('programs.programInfo.tabInstructions')"
              :disable="programIsDeleted"
            />
            <q-tab
              v-if="hasTherapistInstructions"
              :name="tabs.therapistInstructions"
              :label="$t('programs.programInfo.tabTherapistInstructions')"
              :disable="programIsDeleted"
            />
            <q-tab
              v-if="hasProgramTargets"
              :name="tabs.targets"
              :label="$t('programs.programInfo.tabTargets')"
              :disable="programIsDeleted"
            />
            <q-tab
              v-if="hasHistory && programHasClient"
              :name="tabs.history"
              :label="$t('programs.programInfo.tabHistory')"
              :disable="programIsDeleted || offlineMode"
            />
            <q-tab
              v-if="hasProgramMods"
              :name="tabs.programMods"
              :label="$t('programs.programInfo.tabProgramMods')"
              :disable="offlineMode"
            />
          </q-tabs>
        </q-card-section>
        <q-card-section
          class="scroll flex-1 q-pt-none"
          :class="{
            'q-pa-none': $q.platform.is.mobile,
            'q-pa-sm': !$q.platform.is.mobile,
            'macos-scrollbar-padding': $q.platform.is.mac,
          }"
        >
          <q-tab-panels
            v-model="tab"
            animated
            class="col-12"
          >
            <q-tab-panel
              name="content"
              class="q-pa-none"
            >
              <banner
                v-if="programIsDeleted"
                :message="$t('programs.programInfo.programDeleted')"
              />

              <template v-else>
                <q-card-section
                  v-if="hasDemoVideos && !offlineMode"
                  class="row q-pa-none q-col-gutter-md"
                >
                  <div
                    class="col-12"
                  >
                    <program-video
                      v-if="currentDemoVideo"
                      :video-info="currentDemoVideo"
                    />
                  </div>
                  <template
                    v-if="hasMultipleDemoVideos"
                  >
                    <div
                      v-for="demoVideoItem in visibleDemoVideos"
                      :key="demoVideoItem.src"
                      class="col-4"
                    >
                      <q-img
                        :src="demoVideoItem.coverphoto || 'program-placeholder.png'"
                        :ratio="16/9"
                        class="cursor-pointer"
                        @click="onVideoChange(demoVideoItem)"
                      />

                      <div
                        v-if="demoVideoItem === currentDemoVideo"
                        class="bg-red-7 q-pa-xs text-white ellipsis"
                      >
                        {{ $t('programs.programInfo.videoNowPlaying') }}
                      </div>
                    </div>
                  </template>
                </q-card-section>
                <q-card-section
                  v-if="program.objective"
                  class="q-px-none"
                >
                  <div class="text-uppercase text-grey-6 text-weight-bold text-caption q-mb-sm">
                    {{ $t('templates.program.objective') }}
                  </div>
                  <div class="q-mb-md">
                    {{ personalize(program.objective, client) }}
                  </div>
                  <q-separator />
                </q-card-section>
                <q-card-section
                  v-if="hasFilledProgramDataFields"
                  class="q-pa-none"
                >
                  <div
                    v-for="field in filledProgramDataFields"
                    :key="field.id"
                    class="q-mb-md"
                  >
                    <div class="text-uppercase text-grey-6 text-weight-bold text-caption q-mb-sm">
                      {{ field.label }}
                    </div>
                    <div
                      class="q-mb-md"
                      style="white-space: pre-line;"
                    >
                      {{ field.value }}
                    </div>
                    <q-separator />
                  </div>
                </q-card-section>
                <!--eslint-disable vue/no-v-text-v-html-on-component-->
                <q-card-section
                  v-close-popup
                  class="q-pa-none editor-content"
                  v-html="personalize(program.instructions, client)"
                />
                <!--eslint-enable vue/no-v-text-v-html-on-component-->
                <q-card-section
                  v-if="hasLearningMaterials && !offlineMode"
                  class="row q-px-none q-py-md q-col-gutter-md"
                >
                  <div
                    v-for="learningMaterialItem in learningMaterials"
                    :key="learningMaterialItem.id"
                    class="col-4"
                  >
                    <learning-material-asset
                      :file-info="learningMaterialItem"
                      :all-files="learningMaterials"
                    />
                  </div>
                </q-card-section>
              </template>
            </q-tab-panel>
            <q-tab-panel
              name="therapistInstructions"
              class="q-pa-none"
            >
              <!--eslint-disable vue/no-v-text-v-html-on-component-->
              <q-card-section
                v-close-popup
                class="q-pa-none full-height editor-content"
                v-html="personalize(program.therapistInstructions, client)"
              />
              <!--eslint-enable vue/no-v-text-v-html-on-component-->
            </q-tab-panel>
            <q-tab-panel
              name="targets"
              class="q-pa-none"
            >
              <q-skeleton
                v-if="loadingSpinner"
                height="200px"
                square
                class=" q-mb-md"
              />
              <q-card
                v-if="loadingSpinner"
                class="q-pa-md"
              >
                <q-skeleton
                  type="text"
                  class="text-subtitle1"
                  style="min-width:25vw;"
                />
                <q-skeleton
                  type="text"
                  class="text-subtitle1"
                  style="min-width:25vw;"
                />
              </q-card>
              <q-form
                class="q-px-xs"
                @submit="onSaveTargets"
              >
                <target-list
                  v-show="hasProgramTargets"
                  v-model="programTargets"
                  :program="program"
                  :disable-discriminative-stimulus="!hasDiscriminativeStimulus"
                  :program-workflow="programWorkflow"
                  no-bulk-actions
                  read-only
                  :disable="!userCanEditTargetStatuses"
                  disable-add-target
                />
                <div class="row ">
                  <div
                    v-if="userCanAddTargets"
                    class="col-12 row q-mb-md"
                  >
                    <q-btn
                      color="primary"
                      size="md"
                      :disable="offlineMode"
                      :label="$t('editProgram.buttonAddTarget')"
                      class="full-width"
                      @click="onAddTarget"
                    />
                  </div>
                  <div class="col-12 row justify-end">
                    <template v-if="showSaveButton">
                      <q-btn
                        v-close-popup
                        flat
                        :label="$t('common.buttonCancel')"
                        color="secondary"
                      />
                      <q-btn
                        flat
                        :loading="loadingSpinner"
                        :label="$t('common.buttonSave')"
                        :disable="offlineMode"
                        type="submit"
                        color="primary"
                        data-e2e="button-save"
                      />
                    </template>
                    <q-btn
                      v-else
                      v-close-popup
                      flat
                      :label="$t('common.buttonClose')"
                      color="primary"
                    />
                  </div>
                </div>
              </q-form>
            </q-tab-panel>
            <q-tab-panel
              v-if="hasHistory && programHasClient"
              name="history"
              class="q-pa-none "
            >
              <template
                v-if="!noSessionHistorySessions || sessionHistoryIsLoading"
              >
                <div
                  class="q-pb-md row q-col-gutter-xs"
                >
                  <div class="col-12 q-col-gutter-xs row">
                    <q-input
                      :model-value="sessionHistorySessionRunDateFormatted"
                      outlined
                      dense
                      readonly
                      :loading="sessionHistoryIsLoading"
                      :label="$t('sessions.sessionCard.inputSessionDateLabel')"
                      class="col-xs-6 col-sm-auto col-md-5"
                    />
                    <q-input
                      :model-value="sessionHistorySessionRunTimeFormatted"
                      readonly
                      dense
                      hide-bottom-space
                      outlined
                      :loading="sessionHistoryIsLoading"
                      :label="$t('sessions.sessionCard.inputSessionStartLabel')"
                      stack-label
                      class="col-xs-3 col-sm"
                    />
                    <q-input
                      :model-value="sessionHistorySessionFinishTimeFormatted"
                      readonly
                      hide-bottom-space
                      dense
                      outlined
                      :loading="sessionHistoryIsLoading"
                      :label="$t('sessions.sessionCard.inputSessionFinishLabel')"
                      stack-label
                      class="col-xs-3 col-sm"
                    />
                  </div>
                  <div class="col-12 q-col-gutter-xs row">
                    <q-input
                      v-show="sessionHistorySessionSessionDurationFormatted !== null"
                      :model-value="sessionHistorySessionSessionDurationFormatted"
                      readonly
                      dense
                      hide-bottom-space
                      outlined
                      :loading="sessionHistoryIsLoading"
                      :label="$t('sessions.sessionCard.inputSessionDurationLabel')"
                      mask="#:##:##"
                      fill-mask="0"
                      class="col-xs-3 col-sm-6 col-md-auto"
                    />
                    <q-input
                      :model-value="sessionHistorySessionSessionRunByName"
                      readonly
                      dense
                      hide-bottom-space
                      outlined
                      :loading="sessionHistoryIsLoading"
                      :label="$t('sessions.sessionCard.inputSessionRunByLabel')"
                      class="col-xs-9 col-sm-6 col-md ellipsis"
                    />
                  </div>
                  <div class="col-12">
                    <banner
                      v-if="isSessionHistorySessionInProgress"
                      show-icon
                      :message="$t('sessions.sessionCard.warningInProgress')"
                    />
                  </div>
                </div>
                <trial-overview
                  :session="sessionHistorySession"
                  :session-program="sessionHistorySessionProgram"
                  :program="program"
                  :data-notes="sessionHistoryDataNotes"
                  :time="sessionHistoryTime"
                  :is-loading="sessionHistoryIsLoading"
                  :no-data-message="$t('programs.programInfo.noDataMessage')"
                  :can-show-info="canShowInfo"
                />
              </template>
              <div
                v-else-if="noSessionHistorySessions && !sessionHistoryIsLoading"
                class="full-width q-my-xl text-center"
              >
                {{ $t('programs.programInfo.noSessionsMessage') }}
              </div>
            </q-tab-panel>
            <q-tab-panel
              v-if="hasProgramMods"
              name="programMods"
            >
              <activity-feed
                dense
                :events="['programCreation', 'programModification']"
                :filter="[['eventInfo.programId', '==', program.id]]"
                :no-activities-label="$t('programs.programInfo.noProgramModsLabel')"
                :client-id="program.client"
              />
            </q-tab-panel>
          </q-tab-panels>
        </q-card-section>
        <q-card-actions
          v-if="hasAutomationActions"
          align="right"
          class="col-auto q-px-md row q-col-gutter-md"
        >
          <div
            v-for="(programAutomationAction, index) in normalProgramAutomationActions"
            :key="index"
          >
            <automation-action
              :automation-action="programAutomationAction"
              @done="onRunAutomationAction"
            />
          </div>
        </q-card-actions>
        <q-card-actions
          v-else-if="tab==='history'"
          class="col-auto row"
        >
          <div>
            <q-btn
              flat
              color="dark"
              :label="$t('programs.programInfo.btnPrevious')"
              :disable="isFirstSessionHistorySession || sessionHistoryIsLoading"
              icon="navigate_before"
              @click="onPreviousSessionHistorySession"
            />
          </div>
          <q-space />
          <div>
            <q-btn
              flat
              color="dark"
              :label="$t('programs.programInfo.btnNext')"
              :disable="isLastSessionHistorySession || sessionHistoryIsLoading"
              icon-right="navigate_next"
              @click="onNextSessionHistorySession"
            />
          </div>
        </q-card-actions>
      </q-card>
    </div>
  </q-dialog>
</template>
<script>
import ActivityFeed from 'components/Activity/ActivityFeed';
import AutomationAction from 'components/Programs/Automation/AutomationAction';
import BehaviorTrackingIcon from 'components/Programs/BehaviorTrackingIcon';
import ProgramVideo from 'components/Programs/ProgramVideo';
import TargetList from 'components/Programs/TargetList';
import Banner from 'components/UI/Banner';
import { chain, get, has, head, isEmpty, map, reject, size } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import { useTime } from 'src/composables/time';
import { ACTIVE_PROGRAM_STATUSES } from 'src/constants/programs';
import DataDB from 'src/fb/data-db';
import DataNotesDB from 'src/fb/dataNotes-db';
import ProgramAutomationActionsDB from 'src/fb/programAutomationActions-db';
import ProgramFilesDB from 'src/fb/programFiles-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import SessionsDB from 'src/fb/sessions-db';
import Auth from 'src/mixins/Auth';
import FiltersMixin from 'src/mixins/FiltersMixin';
import Interactions from 'src/mixins/Interactions';
import Personalize from 'src/mixins/Personalize';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { dataFields } from 'src/store/dataFields';
import { network } from 'src/store/network';
import { programsTargets } from 'src/store/programsTargets';
import { workflows } from 'src/store/workflows';
import { logEvent } from 'src/utils/LogEvent';
import { isBehaviorTrackingProgram } from 'src/utils/ProgramUtils';
import { defineAsyncComponent } from 'vue';

export default {
  name: 'ProgramInfo',

  components: {
    ActivityFeed,
    AutomationAction,
    BehaviorTrackingIcon,
    LearningMaterialAsset: defineAsyncComponent(() => import('components/Programs/LearningMaterialAsset')),
    ProgramVideo,
    TargetList,
    // made it async to fix problem with its initialization
    TrialOverview: defineAsyncComponent(() => import('components/Sessions/TrialOverview')),
    Banner,
  },

  mixins: [
    Auth,
    FiltersMixin,
    Interactions,
    Personalize,
    Targets,
    Toolbox,
  ],

  props: {
    program: {
      type: Object,
      required: true,
    },

    defaultTab: {
      type: String,
      default: null,
    },

    client: {
      type: Object,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { getLocaleDateString, formatDate } = useTime();
    return { getLocaleDateString, formatDate };
  },

  data() {
    const maximized = this.$q.platform.is.mobile
      || (localStorage.getItem('programInfo_maximized') === 'true');

    const tabs = {
      content: 'content',
      therapistInstructions: 'therapistInstructions',
      targets: 'targets',
      history: 'history',
      programMods: 'programMods',
    };

    return {
      tab: tabs.content,
      tabs,

      programTargets: [],
      programAutomationActions: [],
      demoVideos: [],
      learningMaterials: [],
      currentDemoVideo: {},
      unsavedChanges: false,

      maximized,

      sessionHistoryIsLoading: false,
      sessionHistoryCurrentTimestamp: null,
      sessionHistorySession: {},
      sessionHistorySessionProgram: {},
      sessionHistoryDataNotes: [],
      sessionHistoryTime: 0,
      sessionHistoryFirstSessionId: null,
      sessionHistoryLastSessionId: null,

      loadingSpinner: false,
      canShowInfo: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'hasTherapistCredentials', 'isManager', 'isManagerOrAdministrator',
      'isSupervisor', 'isParent', 'isAdministrator']),
    ...mapState(network, ['offlineMode']),
    ...mapState(dataFields, ['visibleProgramDataFields']),

    filledProgramDataFields() {
      return this.visibleProgramDataFields
        .filter((field) => {
          if (!has(this.program.customData, field.id) || !field.showInSession) {
            return false;
          }
          if (field.hideByDefault) {
            return this.program.shownCustomData?.includes(field.id);
          }
          return !this.program.hiddenCustomData?.includes(field.id);
        })
        .map((field) => ({
          label: field.label,
          value: this.formatCustomDataField(field, this.program.customData),
        }))
        .filter((field) => field.value || ['number', 'boolean'].includes(typeof field.value));
    }, // filledProgramDataFields()

    hasFilledProgramDataFields() {
      return this.filledProgramDataFields.length > 0;
    }, // hasFilledProgramDataFields()

    userCanRunAutomationActions() {
      return this.hasTherapistCredentials;
    }, // userCanAddTargets()

    userCanAddTargets() {
      if (this.program.client === null) {
        // Library programs cannot be edited from preview
        return false;
      }

      if (get(this.program, 'isLocked', null)) {
        return this.isManager
          || ((this.isAdministrator || this.isSupervisor)
            && get(this.userDetails, 'userId') === get(this.program, 'createdByUser'));
      }

      return this.userHasPrivilege('programs/addTarget');
    }, // userCanAddTargets()

    userCanEditTargetStatuses() {
      if (this.program.client === null) {
        // Library programs cannot be edited from preview
        return false;
      }

      if (get(this.program, 'isLocked', null)) {
        return this.isManager
          || ((this.isAdministrator || this.isSupervisor)
            && get(this.userDetails, 'userId') === get(this.program, 'createdByUser'));
      }

      return this.userHasPrivilege('programs/editTargetStatus');
    }, // userCanEditTargetStatuses()

    hasTherapistInstructions() {
      return !!this.program.therapistInstructions && !this.isParent;
    }, // hasTherapistInstructions()

    showSaveButton() {
      return this.userCanAddTargets || this.userCanEditTargetStatuses;
    }, // showSaveButton()

    hasDiscriminativeStimulus() {
      return chain(this.programTargets).map('discriminativeStimulus').some().value();
    }, // hasDiscriminativeStimulus()

    hasProgramTargets() {
      return !['instructions'].includes(this.program.type)
        && (size(this.programTargets) > 0);
    }, // hasProgramTargets()

    hasHistory() {
      return size(this.programTargets) > 0;
    }, // hasHistory()

    hasProgramMods() {
      return (this.program.client)
        && (this.program.type !== 'behaviour')
        && !this.isParent;
    }, // hasProgramMods()

    hasAutomationActions() {
      return (this.normalProgramAutomationActions.length > 0)
        && this.userCanRunAutomationActions
        && ACTIVE_PROGRAM_STATUSES.includes(this.program.status);
    }, // hasAutomationActions()

    hasDemoVideos() {
      return this.visibleDemoVideos.length > 0;
    }, // hasDemoVideos()

    hasMultipleDemoVideos() {
      return this.visibleDemoVideos.length > 1;
    }, // hasMultipleDemoVideos

    hasLearningMaterials() {
      return this.learningMaterials.length > 0;
    }, // hasLearningMaterials()

    normalProgramAutomationActions() {
      return reject(
        this.programAutomationActions,
        (programAutomationAction) => !!programAutomationAction.buttonType,
      );
    }, // normalProgramAutomationActions()

    visibleDemoVideos() {
      const userRole = get(this.userDetails, 'role', '');

      return this.isManagerOrAdministrator
        ? this.demoVideos
        : this.demoVideos.filter((demoVideo) => (
          !demoVideo.limitedVisibility
          || (demoVideo.visibility.includes(userRole))
        ));
    }, // visibleDemoVideos

    isFirstSessionHistorySession() {
      return (get(this.sessionHistorySession, 'id', null) === this.sessionHistoryFirstSessionId);
    }, // isFirstSessionHistorySession

    isLastSessionHistorySession() {
      return (get(this.sessionHistorySession, 'id', null) === this.sessionHistoryLastSessionId);
    }, // isLastSessionHistorySession

    isSessionHistorySessionInProgress() {
      return (get(this.sessionHistorySession, 'status', null) === 'running');
    },

    sessionHistorySessionRunDateFormatted() {
      return this.sessionHistorySession?.runTimestamp
        ? this.getLocaleDateString(this.sessionHistorySession.runTimestamp) : '';
    }, // sessionHistorySessionRunDateFormatted

    sessionHistorySessionRunTimeFormatted() {
      return get(this.sessionHistorySession, 'runTimestamp')
        ? this.formatDate(get(this.sessionHistorySession, 'runTimestamp').toDate(), 'HH:mm')
        : '';
    }, // sessionHistorySessionRunTimeFormatted

    sessionHistorySessionFinishTimeFormatted() {
      return get(this.sessionHistorySession, 'finishTimestamp')
        ? this.formatDate(get(this.sessionHistorySession, 'finishTimestamp').toDate(), 'HH:mm')
        : '';
    }, // sessionHistorySessionFinishTimeFormatted

    sessionHistorySessionSessionDurationFormatted() {
      if (get(this.sessionHistorySession, 'status') === 'running') {
        return null;
      }
      const duration = get(
        this.sessionHistorySession,
        'duration',
        get(this.sessionHistorySession, ['finishTimestamp', 'seconds'], 0)
        - get(this.sessionHistorySession, ['runTimestamp', 'seconds'], 0),
      );
      return this.formatDuration(duration);
    }, // sessionHistorySessionSessionDurationFormatted()

    sessionHistorySessionSessionRunByName() {
      return get(this.sessionHistorySession, 'runByName', '');
    }, // sessionHistorySessionSessionRunByName

    noSessionHistorySessions() {
      return isEmpty(this.sessionHistorySession);
    }, // noSessionHistorySessions()

    programIsDeleted() {
      return this.program.isDeleted;
    }, // programIsDeleted()

    programHasClient() {
      return !!this.program.client;
    }, // programHasClient()

    programWorkflow() {
      return this.getActiveWorkflow(this.program, 'workflow');
    }, // programWorkflow()

  },

  created() {
    if (!this.program) {
      return;
    }

    this.reloadDemoVideos(this.program);
    this.reloadLearningMaterials(this.program);
    this.reloadProgramAutomationActions(this.program);
    this.reloadProgramTargets(this.program)
      .then(() => {
        if (!this.offlineMode) {
          this.reloadProgramWorkflows(this.program, this.programTargets);
        }

        this.applyDefaultTab();
        this.onSwitchTab(this.tab);
      });
  },

  methods: {
    ...mapActions(programsTargets, ['getAllProgramTargets', 'invalidateProgramTargetsCache']),
    ...mapActions(workflows, ['reloadWorkflows']),

    isBehaviorTrackingProgram,

    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    onCloseFullscreen() {
      this.maximized = false;
      localStorage.removeItem('programInfo_maximized');
    }, // onCloseFullscreen()

    onMaximize() {
      this.maximized = true;
      localStorage.setItem('programInfo_maximized', 'true');
    }, // onMaximize()

    onVideoChange(selectedVideo) {
      this.currentDemoVideo = selectedVideo;
    }, // onVideoChange()

    onRunAutomationAction() {
      this.$emit('ok');
      this.hide();
    }, // onRunAutomationAction()

    onAddTarget() {
      this.$q.dialog({
        title: this.$t('programs.programInfo.dialogAddTargetTitle'),
        message: this.$t('programs.programInfo.dialogAddTargetMessage'),
        prompt: {
          model: '',
          isValid: (val) => val.length > 0,
          type: 'text',
        },
        cancel: true,
        persistent: false,
      }).onOk((targetLabel) => {
        if (targetLabel.trim() !== '') {
          const prevOrder = chain(this.programTargets)
            .maxBy('order')
            .get('order')
            .defaultTo(0)
            .value();

          const newTarget = {
            id: uid(),
            type: 'label',
            label: targetLabel,
            status: 'probe',
            parentTarget: null,
            editStatus: 'create',
            order: prevOrder + 1,
          };

          this.programTargets.push(newTarget);
        }
      });
    }, // onAddTarget()

    async onSaveTargets() {
      this.saveProgramTargets();
    }, // onSaveTargets()

    applyDefaultTab() {
      if (!Object.values(this.tabs).includes(this.defaultTab)) {
        return;
      }

      const tabsToAccess = {
        [this.tabs.content]: !this.programIsDeleted,
        [this.tabs.therapistInstructions]: this.hasTherapistInstructions && !this.programIsDeleted,
        [this.tabs.targets]: this.hasProgramTargets && !this.programIsDeleted,
        [this.tabs.history]: this.hasHistory && this.programHasClient && !this.offlineMode && !this.programIsDeleted,
        [this.tabs.programMods]: this.hasProgramMods && !this.offlineMode,
      };

      if (tabsToAccess[this.defaultTab]) {
        this.tab = this.defaultTab;
      }
    }, // applyDefaultTab()

    async onSwitchTab(tab) {
      if (tab === this.tabs.history) {
        if (await this.loadSessionHistory()) {
          this.sessionHistoryLastSessionId = get(this.sessionHistorySession, 'id', null);
        }
      }
    }, // onSwitchTab()

    async onPreviousSessionHistorySession() {
      const currentRunTimestamp = get(this.sessionHistorySession, 'runTimestamp');
      if (!currentRunTimestamp) {
        return;
      }
      if (!(await this.loadSessionHistory(this.sessionHistoryCurrentTimestamp, 'desc'))) {
        this.sessionHistoryFirstSessionId = get(this.sessionHistorySession, 'id', null);
      }
    }, // onPreviousSessionHistorySession()

    async onNextSessionHistorySession() {
      const currentRunTimestamp = get(this.sessionHistorySession, 'runTimestamp');
      if (!currentRunTimestamp) {
        return;
      }
      if (!(await this.loadSessionHistory(this.sessionHistoryCurrentTimestamp, 'asc'))) {
        this.sessionHistoryLastSessionId = get(this.sessionHistorySession, 'id', null);
      }
    }, // onNextSessionHistorySession()

    async loadSessionHistory(currentTimestamp = null, direction = 'desc', increment = 0) {
      if (increment > 10) {
        // Guard to prevent infinite loading
        return false;
      }

      this.sessionHistoryIsLoading = true;
      let resetLoader = true;

      try {
        const dataConstraint = [
          ['client', '==', this.program.client],
          ['program', '==', this.program.id],
        ];
        const directionComparator = (direction === 'desc') ? '<' : '>';
        if (currentTimestamp) {
          dataConstraint.push([
            'createTimestamp', directionComparator, currentTimestamp,
          ]);
        }

        const dataDb = new DataDB();
        const [data] = await dataDb.readAll(dataConstraint, 'createTimestamp', direction, 1);

        if (!data) {
          return false;
        }

        if (data.isDeleted) {
          const nextTimestamp = data.createTimestamp || null;

          if (!nextTimestamp) {
            return false;
          }

          resetLoader = false;

          return this.loadSessionHistory(nextTimestamp, direction, increment + 1);
        }

        this.sessionHistoryCurrentTimestamp = get(data, 'createTimestamp', null);

        const sessionsDb = new SessionsDB();
        const session = await sessionsDb.read(data.session);

        if (!session) {
          return false;
        }

        if (!['running', 'done', 'processing'].includes(get(session, 'status'))) {
          const nextTimestamp = currentTimestamp || get(data, 'createTimestamp', null);
          if (!nextTimestamp) {
            return false;
          }
          return this.loadSessionHistory(nextTimestamp, direction, increment + 1);
        }

        const sessionProgramsDb = new SessionProgramsDB(session.id);
        const sessionHistorySessionProgram = await sessionProgramsDb.read(get(data, 'sessionProgram', null));
        if (sessionHistorySessionProgram) {
          sessionHistorySessionProgram.data = data;
        }

        const dataNotesDb = new DataNotesDB();
        const sessionHistoryDataNotes = await dataNotesDb.readAll(
          [['client', '==', this.program.client],
            ['program', '==', this.program.id],
            ['session', '==', session.id]],
        );

        this.sessionHistorySessionProgram = sessionHistorySessionProgram || {};
        this.sessionHistoryDataNotes = sessionHistoryDataNotes
          ?.sort((first, second) => first.createTimestamp - second.createTimestamp) || [];
        this.sessionHistorySession = session;
        this.sessionHistoryTime = 500000;
      } catch (err) {
        this.notifyNegative(err.message);
        logEvent(err, {
          context: {
            session_data: {
              programId: this.program.id,
            },
          },
        });
      } finally {
        if (resetLoader) {
          this.sessionHistoryIsLoading = false;
        }
      }

      return true;
    }, // loadSessionHistory()

    async saveProgramTargets() {
      this.loadingSpinner = true;

      this.invalidateProgramTargetsCache({ program: this.program.id });

      await this.saveTargetChanges(this.programTargets, this.program.id);
      await this.updateTargetsPerStatus(this.program);

      this.unsavedChanges = false;
      this.loadingSpinner = false;
      this.$emit('ok', true);
      this.hide();
    }, // saveProgramTargets()

    async reloadDemoVideos(program) {
      if (!program || isEmpty(program)) {
        return;
      }
      this.loadingSpinner = true;
      try {
        if (program.demoVideo) {
          // Convert old demo-video to new format for display
          this.demoVideos = [{
            program: this.program.id,
            programFileType: 'instructionsVideo',

            src: program.demoVideo,
            filename: 'Demo',
            hidden: false,
            order: 1,

            limitedVisibility: false,
            visibility: [],
          }];
        }

        const programFilesDb = new ProgramFilesDB(program.id);
        this.demoVideos = await programFilesDb.readAll(
          [
            ['programFileType', '==', 'instructionsVideo'],
          ],
          'order',
        );
      } finally {
        this.loadingSpinner = false;
      }
      this.currentDemoVideo = head(this.visibleDemoVideos);
    }, // reloadDemoVideos

    async reloadLearningMaterials(program) {
      if (!program || isEmpty(program)) {
        return;
      }
      this.loadingSpinner = true;
      try {
        const programFilesDb = new ProgramFilesDB(program.id);
        this.learningMaterials = await programFilesDb.readAll(
          [
            ['programFileType', '==', 'learningMaterials'],
          ],
          'order',
        );
      } finally {
        this.loadingSpinner = false;
      }
    }, // reloadLearningMaterials()

    async reloadProgramTargets(program) {
      this.loadingSpinner = true;
      this.programTargets = await this.getAllProgramTargets({ program: program.id });
      this.loadingSpinner = false;
    }, // reloadProgramTargets()

    async reloadProgramWorkflows(program, programTargets) {
      const workflowIds = chain(programTargets)
        .map('workflow')
        .concat(program.workflow)
        .concat(
          map(programTargets, 'defaultWorkflow'),
        )
        .uniq()
        .compact()
        .value();
      return this.reloadWorkflows(workflowIds);
    }, // reloadProgramWorkflows()

    async reloadProgramAutomationActions(program) {
      if (!program || isEmpty(program)) {
        return;
      }

      try {
        const programAutomationActionsDb = new ProgramAutomationActionsDB(program.id);
        this.programAutomationActions = await programAutomationActionsDb.readAll('', 'order');
      } catch {
        this.programAutomationActions = [];
      }
    }, // reloadProgramAutomationActions

  },

};
</script>
<style scoped>
.macos-scrollbar-padding {
  padding-right: 20px;
}

.dialog-height-non-mobile {
  height: 75vh;
}

.dialog-height-mobile {
  height: 100vh;
}

.flex-1 {
 flex: 1;
}

.overflow-hidden-x {
  overflow-x: hidden;
}
</style>
